import { take, fork, put, call, cancel } from "redux-saga/effects";
import { loginFail, loginSuccess, logout } from "store/actions/login";
import types from "store/actionTypes";
import { callApi } from "store/sagas/callApi";
import LocalStorage from "services/localStorage";
import { accountRoleIdsByName as Roles } from "configs/parameters";
import { proxy } from "services/apiService";
import {
  cognitoSignIn,
  PasswordResetRequiredError,
  NewPasswordRequiredError,
} from "services/cognitoAuth";

function* completeLogin(account, permissions, token) {
  if (![Roles.admin, Roles.manager].includes(account.role_id)) {
    LocalStorage.empty();
    yield put(loginFail({ exception: "Permission denied!" }));
    return false;
  }
  LocalStorage.set("token", token);
  LocalStorage.set("account", JSON.stringify(account));
  LocalStorage.set("permissions", JSON.stringify(permissions));
  yield put(
    loginSuccess({
      authenticated: true,
      token,
      account,
      permissions,
    })
  );
  return true;
}

function* processLogin({ email, password, history }) {
  try {
    if (email && password) {
      // 1) Authenticate against Cognito directly from the browser.
      let idToken;
      try {
        idToken = yield call(cognitoSignIn, email, password);
      } catch (e) {
        if (e instanceof NewPasswordRequiredError) {
          // Cognito requires the user to set a new password (admin reset).
          if (history) {
            history.push("/new-password", {
              email: e.email,
              session: e.session,
            });
          }
          return yield put(
            loginFail({
              exception: "Set a new password to continue.",
            })
          );
        }
        if (e instanceof PasswordResetRequiredError) {
          // Cognito requires the user to reset before they can sign in.
          if (history) {
            history.push(`/confirm-reset?email=${encodeURIComponent(e.email)}`);
          }
          return yield put(
            loginFail({
              exception:
                "Password reset required. Check your email for a verification code.",
            })
          );
        }
        return yield put(
          loginFail({ exception: e?.message || "Login failed" })
        );
      }

      // 2) Exchange the Cognito ID token for our app JWT.
      const response = yield call(callApi, "ssoLogin", "POST", { idToken });
      const { data } = response;
      if (!data?.success) {
        return yield put(
          loginFail({ exception: data?.message || "Login failed" })
        );
      }
      yield call(completeLogin, data.account, data.permissions, data.token);
      return;
    }

    // No credentials supplied — try restoring from localStorage.
    const localToken = LocalStorage.get("token");
    const permissions = LocalStorage.get("permissions");
    let localAccount = LocalStorage.get("account");

    if (localToken && localAccount) {
      try {
        localAccount = JSON.parse(localAccount);
        const data = { member_id: Number(localAccount.member_id) };
        const response = yield call(proxy.getAccount, "getAccount", data);
        LocalStorage.set("account", JSON.stringify(response.data.result));
        yield put(
          loginSuccess({
            authenticated: true,
            token: localToken,
            account: response.data.result,
            permissions: JSON.parse(permissions),
          })
        );
      } catch (e) {
        console.log(e);
        yield put(logout());
      }
    } else {
      yield put(logout());
    }
  } catch (e) {
    yield put(
      loginFail({ exception: e?.response?.data?.error?.message || e.message })
    );
  }
}

export function* watchLogin() {
  let forked = null;
  while (true) {
    const action = yield take(types.LOGIN_REQUEST);
    if (forked) yield cancel(forked);
    forked = yield fork(processLogin, action);
  }
}

export function* watchLogout() {
  while (true) {
    yield take(types.LOGOUT);
    LocalStorage.empty();
  }
}
